import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import ProtectedRoute from "./pages/ProtectedRoute";
import { initPhoneTracking } from "./lib/analytics";
import { LOCALITY_LIST } from "./constants/locals";
import { SERVICE_PAGE_LIST } from "./constants/servicePages";
import { COMBO_PAGE_LIST } from "./constants/combos";

const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Projects = lazy(() => import("./pages/Projects"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const QuoteForm = lazy(() => import("./pages/QuoteForm"));
const QuoteConfirmation = lazy(() => import("./pages/QuoteConfirmation"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const RequestDetail = lazy(() => import("./pages/RequestDetail"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const LocalPage = lazy(() => import("./pages/LocalPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ComboPage = lazy(() => import("./pages/ComboPage"));

const PageLoader = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="page-loader">
    <div className="w-8 h-8 border-2 border-[#262626] border-t-primary rounded-full animate-spin" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback (session_id in URL fragment) before any routing.
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/services" element={<Layout><Services /></Layout>} />
        <Route path="/projets" element={<Layout><Projects /></Layout>} />
        <Route path="/a-propos" element={<Layout><About /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/soumission" element={<Layout><QuoteForm /></Layout>} />
        <Route path="/soumission/merci" element={<Layout><QuoteConfirmation /></Layout>} />
        <Route path="/blog" element={<Layout><Blog /></Layout>} />
        <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
        {LOCALITY_LIST.map((l) => (
          <Route key={l.slug} path={`/${l.slug}`} element={<Layout><LocalPage slug={l.slug} /></Layout>} />
        ))}
        {SERVICE_PAGE_LIST.map((s) => (
          <Route key={s.slug} path={`/${s.slug}`} element={<Layout><ServicePage slug={s.slug} /></Layout>} />
        ))}
        {COMBO_PAGE_LIST.map((c) => (
          <Route key={c.slug} path={`/${c.slug}`} element={<Layout><ComboPage slug={c.slug} /></Layout>} />
        ))}
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
        <Route path="/admin/demandes/:id" element={<ProtectedRoute><RequestDetail /></ProtectedRoute>} />
        <Route path="*" element={<Layout><Home /></Layout>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    initPhoneTracking();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
